<script setup lang="ts">
import Calendar from 'primevue/calendar';
import { toRef, computed } from 'vue';
import { useField } from 'vee-validate';

interface Props {
  placeholder?: string;
  label?: string;
  hint?: string;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  manualInput?: boolean;
  name?: string;
  tooltip?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'mm/dd/yyyy',
  label: '',
  hint: '',
  dateFormat: 'mm/dd/yy',
  minDate: () => new Date(),
  manualInput: true,
  name: '',
  tooltip: '',
});

const emit = defineEmits(['date-select']);
const { value: dueDateValue } = useField<Date | string | null>(
  toRef(props, 'name'),
);

const dueDate = computed<Date | string | undefined>({
  get: () => dueDateValue.value ?? undefined,
  set(val: Date | string | undefined) {
    dueDateValue.value = val ?? null;
  },
});

const handleDateSelect = (selectedDate: any) => {
  if (props.name) {
    dueDateValue.value = selectedDate;
  }
  emit('date-select', selectedDate);
};
</script>

<template>
  <div>
    <div :class="$style.label">
      {{ props.label }}
      <button
        v-if="props.tooltip"
        v-tooltip.focus="{ value: props.tooltip, escape: true }"
        class="pi pi-info-circle"
        :class="$style.tooltipIcon"
        type="button"
      ></button>
    </div>
    <div :class="$style.inputGroup">
      <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
        <i class="pi pi-calendar"></i>
      </div>
      <Calendar
        v-model="dueDate"
        :dateFormat="props.dateFormat"
        :placeholder="props.placeholder"
        :class="$style.calendarInputField"
        :minDate="props.minDate"
        :maxDate="props.maxDate"
        :manualInput="props.manualInput"
        @date-select="handleDateSelect"
      />
    </div>
    <div v-if="props.hint" :class="$style.hint">{{ props.hint }}</div>
  </div>
</template>

<style lang="less" module>
.inputGroup {
  display: flex;
  height: 50px;
  max-width: 100%;
  input {
    border-radius: 0;
  }
}
.inputGroupAddonLeft {
  border-left: 1px solid #d1d5db;
  border-radius: 6px 0 0 6px !important;
}
.inputGroupAddon {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 14px;
  border-top: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;
}
.calendarInputField {
  flex: 1;
  :global(input) {
    border-radius: 0 6px 6px 0;
  }
}
.label {
  font-size: 14px;
  height: 20px;
  font-weight: 500;
  margin-bottom: 4px;
}
.hint {
  font-size: 14px;
  line-height: 20px;
  color: #bbbbbc;
}

.tooltipIcon {
  font-size: 14px;
  cursor: help;
  display: inline-flex;
  align-items: center;
  color: @color-black;
}
</style>
